import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Drawer, IconButton, Tab, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PlayerAvatar from './PlayerAvatar';
import { PLAYER_EFFECTS } from '../services/PlayerEffects';
import { customStorage } from '../services/CommonService';
import { STO_PLAYER_EFFECT } from '../constants/commonConstants';
import { useTranslation } from 'react-i18next';
import './PlayerInteractionDialog.scss';

const FALLBACK_HEIGHT_RATIO = 0.4;
const MAX_DRAWER_HEIGHT = 360;
const DEFAULT_EFFECT_ID = 'tomato';

const savedEffect = () => {
  try {
    const effectId = customStorage.persistent.getItem(STO_PLAYER_EFFECT);
    return PLAYER_EFFECTS.find(effect => effect.id === effectId) || PLAYER_EFFECTS.find(effect => effect.id === DEFAULT_EFFECT_ID);
  } catch {
    return PLAYER_EFFECTS.find(effect => effect.id === DEFAULT_EFFECT_ID);
  }
};

const fallbackBounds = () => {
  const height = Math.min(window.innerHeight * FALLBACK_HEIGHT_RATIO, MAX_DRAWER_HEIGHT);
  return { left: 0, top: window.innerHeight - height, width: window.innerWidth, height };
};

const measureBottomGameRegion = () => {
  const regions = ['.game-action-section', '.hand-wrapper']
    .map(selector => document.querySelector(selector)?.getBoundingClientRect())
    .filter(rect => rect && rect.width > 0 && rect.height > 0);
  if (!regions.length) return fallbackBounds();

  const left = Math.max(0, Math.min(...regions.map(rect => rect.left)));
  const right = Math.min(window.innerWidth, Math.max(...regions.map(rect => rect.right)));
  const bottom = Math.min(window.innerHeight, Math.max(...regions.map(rect => rect.bottom)));
  const contentTop = Math.max(0, Math.min(...regions.map(rect => rect.top)));
  const minimumHeight = Math.min(window.innerHeight * FALLBACK_HEIGHT_RATIO, MAX_DRAWER_HEIGHT);
  const top = Math.max(0, Math.min(contentTop, bottom - minimumHeight));
  return { left, top, width: Math.max(1, right - left), height: Math.max(1, window.innerHeight - top) };
};

export default function PlayerInteractionDialog({ open, player, roomId, onClose, onSend }) {
  const { t } = useTranslation();
  const initialEffect = useMemo(savedEffect, []);
  const [tab, setTab] = useState(initialEffect.category);
  const [selectedEffectId, setSelectedEffectId] = useState(initialEffect.id);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [drawerBounds, setDrawerBounds] = useState(fallbackBounds);
  const effects = useMemo(() => PLAYER_EFFECTS.filter(effect => effect.category === tab), [tab]);

  useEffect(() => {
    if (!open) return;
    const effect = savedEffect();
    setTab(effect.category);
    setSelectedEffectId(effect.id);
    setSending(false);
    setSendError('');
  }, [open, player?.playerId]);

  const selectEffect = effect => {
    setSelectedEffectId(effect.id);
    try { customStorage.persistent.setItem(STO_PLAYER_EFFECT, effect.id); }
    catch { /* Keep the selection for this open drawer when storage is unavailable. */ }
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    let frame;
    const measureNow = () => setDrawerBounds(measureBottomGameRegion());
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measureNow);
    };
    measureNow();
    window.addEventListener('resize', scheduleMeasure);
    window.visualViewport?.addEventListener('resize', scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleMeasure);
      window.visualViewport?.removeEventListener('resize', scheduleMeasure);
    };
  }, [open]);

  const changeTab = (_, nextTab) => {
    setTab(nextTab);
    const first = PLAYER_EFFECTS.find(effect => effect.category === nextTab);
    if (first) selectEffect(first);
  };
  const send = async () => {
    if (!selectedEffectId || sending) return;
    setSending(true);
    setSendError('');
    try {
      await onSend(selectedEffectId);
      onClose();
    } catch {
      setSendError(t('playerEffects.failed'));
    } finally {
      setSending(false);
    }
  };

  return <Drawer anchor="bottom" open={Boolean(open && player)} disableEscapeKeyDown
    className="player-interaction-drawer-root"
    ModalProps={{ keepMounted: true }}
    PaperProps={{
      className: 'player-interaction-drawer-paper',
      style: {
        left: `${drawerBounds.left}px`, top: `${drawerBounds.top}px`,
        width: `${drawerBounds.width}px`, height: `${drawerBounds.height}px`,
      },
    }}>
    <header className="player-interaction-title">
      <Box className="player-interaction-heading">
        <PlayerAvatar roomId={roomId} playerId={player?.playerId} name={player?.playerName}
          sx={{ width: 30, height: 30, fontSize: '.7rem' }} />
        <span>{player?.playerName}</span>
      </Box>
      <IconButton className="dialog-close-button" onClick={onClose} aria-label={t('common.close')} size="small"><CloseIcon /></IconButton>
    </header>
    <Tabs className="player-interaction-tabs" value={tab} onChange={changeTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
      aria-label={t('playerEffects.actions')}>
      <Tab value="throw" label={t('playerEffects.throw')} />
      <Tab value="food" label={t('playerEffects.food')} />
      <Tab value="fun" label={t('playerEffects.fun')} />
      <Tab value="kind" label={t('playerEffects.kind')} />
    </Tabs>
    <div className="player-interaction-content">
      <Typography className="player-effect-instruction">{t('playerEffects.choose')}</Typography>
      {sendError && <Typography color="error" role="alert" className="player-effect-error">{sendError}</Typography>}
      <Box className="player-effect-grid">
        {effects.map(effect => {
          const isSelected = selectedEffectId === effect.id;
          return <button key={effect.id} type="button"
            className={`player-effect-option${isSelected ? ' is-selected' : ''}`}
            onClick={() => selectEffect(effect)} aria-pressed={isSelected}>
            {isSelected && <span className="player-effect-selected-mark" aria-hidden="true">
              <CheckCircleRoundedIcon />
            </span>}
            <Avatar variant="rounded" src={effect.image} alt="" />
            <span className="player-effect-option-name">{t(`playerEffects.items.${effect.id}`)}</span>
          </button>;
        })}
      </Box>
    </div>
    <footer className="player-interaction-actions">
      <Button onClick={onClose}>{t('common.cancel')}</Button>
      <Button variant="contained" disabled={sending || !selectedEffectId} onClick={send}>
        {sending ? t('playerEffects.sending') : t('playerEffects.send')}
      </Button>
    </footer>
  </Drawer>;
}
